<template>
  <div class="widget__content-center widget__content-center--flex">
    <div v-if="chartSeries?.length" class="chart-wrapper">
      <apexchart
        type="bar"
        height="100%"
        width="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { barColors } from '@/utils/chartColors';

import type { WidgetFilter, WidgetSetting, WidgetTypeRepeatVisits } from '@/types/Widget';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeRepeatVisits;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
}>();

// Get array of the Time objects. Each has an ID equal to the
// timestamp and a title equal to the text to display.
const labels = computed(() => {
  const chosenTime = props.time?.name;
  if (!props.widget.data) {
    return [];
  }

  const timeFilter = props.widget.filters?.time;

  if (timeFilter) {
    const dataFilter = timeFilter.find((filter) => filter.name === chosenTime);

    if (chosenTime === 'Day') {
      if (dataFilter?.data) {
        return dataFilter.data;
      }
    }

    if (dataFilter && dataFilter.data && dataFilter.data?.length) {
      return dataFilter.data;
    }
  }

  return [];
});

const chartSeries = computed(() => {
  const areaName = props.area?.name;

  if (!props.widget.data || !areaName) {
    return [];
  }

  // Get the data filtering down by the Area, Time, and Zone
  let settingData = props.widget.data['Site']['Day'];

  if (areaName === 'Zone' && props.time?.name != 'Total' && props.zone) {
    settingData = props.widget.data[areaName][props.zone?.id]['Day'];
  }

  // Data is now an object w/ each property being a unix timestamp
  // Each of those properties has the value of engaged, passerby, &
  // conversion for that time.

  if (!settingData) {
    return null;
  }

  // Restructure Data for use in the chart
  const formatted = Object.keys(settingData).map((timeCode) => {
    return {
      title: labels.value?.find((label) => `${label.id}` === timeCode)?.title,
      data: [settingData[timeCode]['Engaged'], settingData[timeCode]['Passerby']],
    };
  });

  type barData = {
    x: string;
    y: number;
  };

  const engagedData: barData[] = [];
  const passerData: barData[] = [];

  formatted.forEach((char) => {
    engagedData.push({
      x: 'Engaged',
      y: char.data[0],
    });
    passerData.push({
      x: 'Passerby',
      y: char.data[1],
    });
  });

  return [
    {
      name: 'Engaged',
      data: engagedData,
    },
    {
      name: 'Passerby',
      data: passerData,
    },
  ];
});

const chartCategories = computed(() => {
  const label = labels.value;

  if (label) {
    return Object.keys(labels.value).map((key) => label[key as any].title);
  }
  return [];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => ({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'bar',
    stacked: true,
    id: 'meshh-chart-repeat-visits',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      props.time?.name || '',
      props.zone?.title || '',
      props.cluster?.title || '',
      '',
    ),
  },
  colors: barColors,
  xaxis: {
    categories: chartCategories.value,
  },
  grid: {
    show: false,
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0);
      },
    },
  },
}));
</script>
